/**
 * `studio/analytics.html` §3 — the guardrail. Value delivered against value billed,
 * and the effective rate that follows from it.
 *
 * The whole panel is `card--need` when something is under-scoped and a plain card
 * when nothing is (DESIGN.md P4: ochre is "this needs your decision, or this costs
 * you more" — a card that is always ochre says neither). P3 decides what is drawn:
 * the packages that are over are meters, the ones that are merely hot this month are
 * folded, and the rest are in the chart beside them and nowhere else. `scope.ts` is
 * the one place "over", "watch" and "no ratio" are decided.
 *
 * **A meter is a used against an included, never a width** (CONTRACT.md §2.10). An
 * under-scoped block's reading is `ratio` in percentage points against a hundred, so
 * the track runs to 183 with the allowance mark at 100 — which is precisely the
 * geometry `analytics.html` hard-codes as `width:53%` + `width:47%`. Only the part
 * past the hundred is ochre, and only that part is what the studio is giving away.
 *
 * **This is the section the period control moves**, so its head names the month the
 * payload answered for rather than the month the seg is on.
 *
 * **Clients are named by slug**, because `ClientScopeStatusSchema` and
 * `ClientEffectiveRateSchema` carry `client` and no display name. The report asks for
 * `name` on both; `clients_summary` already answers one and is `/studio/clients`'s read.
 */

import { formatList } from '@fareast/shared'

import { HowThisWorks } from '../../../components/client/index.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import {
  BarChart,
  barViewBox,
  Chart,
  Legend,
  LegendKey,
  type ChartSlot,
} from '../../../components/portal/Chart.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { Meter, WHOLE_IN_POINTS, percentagePoints } from '../../../components/portal/Meter.js'
import { RegionCols } from '../../../components/portal/Region.js'
import { Answer, B, Prose } from '../../../components/portal/Text.js'
import { ClientLink } from '../../../components/studio/ClientLink.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import type { AnalyticsReads, HealthData, RateData } from './data.js'
import { UNDER_SCOPED_ID } from './ids.js'
import { Panel } from './Panel.js'
import { rankRates, type RatedRow, type RateRow } from './rates.js'
import { WHOLE_AMOUNT } from './render.js'
import { partitionScope, type ScopeRow } from './scope.js'
import { effectiveRateTable, onScreen } from './tables.js'

/** The meter's top line reads `183 / 100`, not `182.69 / 100`. */
const METER_DIGITS = 0

export type UnderScopedSectionProps = {
  readonly health: AnalyticsReads['health']
  readonly rate: AnalyticsReads['rate']
}

export function UnderScopedSection({ health, rate }: UnderScopedSectionProps) {
  const format = useFormat()
  const partition = health.data === undefined ? null : partitionScope(health.data)

  return (
    <Section id={UNDER_SCOPED_ID}>
      <SectionHead
        title="Under-scoped blocks"
        note={health.data === undefined ? undefined : format.monthLong(health.data.period)}
        pushNote
      />
      <Card need={partition !== null && partition.over.length > 0}>
        <CardBody>
          <Panel query={health} subject="client health">
            {(data) => <ScopeAnswer health={data} />}
          </Panel>

          <RegionCols>
            <Stack>
              {/* Not a second `Panel`: the answer above is already this read's, and
                  one failure printing the same line twice reads as two failures. */}
              {partition === null ? null : <ScopeMeters partition={partition} />}
              <HowThisWorks summary="How under-scoping is measured">
                <Stack gap="tight">
                  <Prose>
                    The meter runs to the value delivered at rate-card prices; the mark at 100 is
                    what the month actually bills. Past it, the studio is doing work nobody is
                    paying for.
                  </Prose>
                  <Prose>
                    One hot month is a watch — onboarding always runs hot. Two months running is an
                    under-scoped block: re-scope it at the next billing day, or price the overflow
                    as an extra.
                  </Prose>
                  <Prose>
                    The effective rate beside it divides the <B>same</B> figure the mark at 100
                    stands for — this month&rsquo;s retainer line plus the extras agreed this month
                    — by this month&rsquo;s retainer hours. Project hours are excluded, so a busy
                    project month does not flatter a thin package, and the money is the
                    month&rsquo;s own rather than what an invoice happened to recognise in it: the
                    run bills extras a month in arrears.
                  </Prose>
                </Stack>
              </HowThisWorks>
            </Stack>

            <Panel query={rate} subject="the effective rate">
              {(data) => <RateChart rate={data} />}
            </Panel>
          </RegionCols>
        </CardBody>
      </Card>
    </Section>
  )
}

/* --------------------------------------------------------------- the meters */

function ScopeAnswer({ health }: { readonly health: HealthData }) {
  const format = useFormat()
  const { over, watch } = partitionScope(health)

  if (over.length > 0) {
    return (
      <Answer>
        <B>
          {`${format.number(over.length)} ${over.length === 1 ? 'package is' : 'packages are'} under-scoped.`}
        </B>{' '}
        {`${formatList(over.map((one) => one.name))} ${over.length === 1 ? 'has' : 'have'} delivered past what the package bills for a second month running.`}
      </Answer>
    )
  }

  return (
    <Answer>
      {watch.length === 0
        ? 'Every live package is delivering inside what it bills for.'
        : `${format.number(watch.length)} ${watch.length === 1 ? 'package went' : 'packages went'} past target this month. One hot month is a watch, not a re-scope.`}
    </Answer>
  )
}

function ScopeMeters({ partition }: { readonly partition: ReturnType<typeof partitionScope> }) {
  const { over, watch, unpriced } = partition

  /*
   * A quiet month draws nothing here at all — not an empty line. The `Answer` above
   * already says every package is inside what it bills for, and DESIGN.md P7 gives an
   * empty state one line, not a second copy of the sentence beside it.
   */
  return (
    <>
      {over.map((row) => (
        <ScopeMeter key={row.client} row={row} />
      ))}

      {watch.length === 0 ? null : (
        <Fold
          summary={`${String(watch.length)}${over.length === 0 ? '' : ' more'} above target, inside tolerance`}
        >
          {watch.map((row) => (
            <ScopeMeter key={row.client} row={row} />
          ))}
        </Fold>
      )}

      {unpriced.length === 0 ? null : (
        <Empty>
          {`${formatList(unpriced.map((one) => one.name))} billed nothing this month, so there is no ratio to read.`}
        </Empty>
      )}
    </>
  )
}

/**
 * One package's meter.
 *
 * The foot does **not** repeat the percentage: `.meter__top` already reads `183 /
 * 100` and the label beside it already carries what the month billed, so a foot
 * saying "183% of what the month bills" was DESIGN.md P2's restatement three lines
 * under its own source.
 */
function ScopeMeter({ row }: { readonly row: ScopeRow }) {
  const format = useFormat()
  /* `partitionScope` filters on a non-null ratio; `0` here would draw an empty track. */
  const reading = row.ratio ?? 0

  return (
    <Meter
      label={
        <ClientLink client={row.client} name={row.name}>
          {`${format.money(row.paid, WHOLE_AMOUNT)} billed this month`}
        </ClientLink>
      }
      used={percentagePoints(reading)}
      included={WHOLE_IN_POINTS}
      fractionDigits={METER_DIGITS}
      foot={
        <>
          {`${format.money(row.value, WHOLE_AMOUNT)} of work at rate-card prices. `}
          {row.months_over > 1
            ? `${format.number(row.months_over)} months running.`
            : 'First month over.'}
          {row.action === null ? null : (
            <>
              {' '}
              <B>Action:</B> {row.action}
            </>
          )}
        </>
      }
    />
  )
}

/* ----------------------------------------------------- the effective rate chart */

/**
 * Under the floor is the one ochre thing here; everything else is de-emphasised.
 *
 * `'need'` rather than `'c1'`: this bar is not category one of anything, it is the
 * row that needs somebody. The two were the same colour until session 24 gave the
 * categorical palette its own slot one, and the comment above was true only by that
 * coincidence.
 */
function slotFor(row: RatedRow): ChartSlot {
  return row.under_floor ? 'need' : 'demph'
}

function RateChart({ rate }: { readonly rate: RateData }) {
  const format = useFormat()
  const { rated, untimed } = rankRates(rate)
  const under = rated.filter((one) => one.under_floor)

  return (
    <Chart
      conclusion={
        rated.length === 0
          ? 'Nobody logged retainer time this month, so there is no effective rate to read.'
          : `Effective dollars per hour by client in ${format.monthLong(rate.period)}: ${rated
              .map((one) => `${one.name} ${format.money(one.rate, WHOLE_AMOUNT)}`)
              .join('; ')}. The floor is ${format.money(rate.floor, WHOLE_AMOUNT)}.`
      }
      viewBox={barViewBox(rated.length)}
      legend={
        <Legend>
          <LegendKey slot="c1">Under the floor — re-scope</LegendKey>
          <LegendKey slot="demph">At or above it</LegendKey>
        </Legend>
      }
      note={rateNote(rate, under, untimed, format)}
      table={onScreen(
        effectiveRateTable(
          rate,
          rated.map((one) => one.client),
          format,
        ),
      )}
    >
      <BarChart
        data={rated.map((one) => ({
          label: one.name,
          value: one.rate.amount,
          slot: slotFor(one),
          valueLabel: format.money(one.rate, WHOLE_AMOUNT),
          title: `${one.name}: ${format.money(one.rate, WHOLE_AMOUNT)} an hour — ${format.money(one.paid, WHOLE_AMOUNT)} billed this month over ${format.number(one.minutes)} retainer minutes${one.under_floor ? ', under the floor' : ''}`,
        }))}
        threshold={{ value: rate.floor.amount }}
        labelWidth={150}
      />
    </Chart>
  )
}

/**
 * The one narration line: where the rule is drawn, and who is on the wrong side of it.
 *
 * `BarChart` draws a threshold as a bare rule with no label — only `ColumnChart`
 * prints one — so the floor has to be said in words, which it would want to be anyway.
 */
function rateNote(
  rate: RateData,
  under: readonly RatedRow[],
  untimed: readonly RateRow[],
  format: Formatter,
): string {
  const floor = `${format.monthLong(rate.period)}’s retainer line plus the extras agreed in it, over that month’s retainer hours. The rule is drawn at ${format.money(rate.floor, WHOLE_AMOUNT)} an hour, retainer time only — project hours are not in it.`
  const below =
    under.length === 0
      ? ' Nobody is under it this month.'
      : ` ${formatList(under.map((one) => one.name))} ${under.length === 1 ? 'is' : 'are'} under it.`
  const missing =
    untimed.length === 0
      ? ''
      : ` ${formatList(untimed.map((one) => one.name))} logged no retainer time, so ${untimed.length === 1 ? 'it has' : 'they have'} no rate.`
  return `${floor}${below}${missing}`
}
